import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const getToday = () => new Date().toISOString().split('T')[0];

const DEFAULT_EXPENSE_FORM = {
  name: '',
  category: 'services',
  amount: '',
  frequency: 'one_time',
  description: '',
  date: getToday()
};

const DEFAULT_SALARY_FORM = {
  name: '',
  role: '',
  amount: '',
  frequency: 'monthly',
  start_date: getToday(),
  end_date: '',
  is_active: true
};

export const useCostManagement = () => {
  const [showCostModal, setShowCostModal] = useState(false);
  const [costData, setCostData] = useState({ expenses: [], salaries: [], totals: {} });
  const [costLoading, setCostLoading] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddSalary, setShowAddSalary] = useState(false);
  const [expenseForm, setExpenseForm] = useState(DEFAULT_EXPENSE_FORM);
  const [salaryForm, setSalaryForm] = useState(DEFAULT_SALARY_FORM);

  const fetchCostData = useCallback(async () => {
    try {
      setCostLoading(true);
      const response = await axios.get(
        `${API}/governance/five-pillars/costs`,
        { headers: getHeaders() }
      );
      setCostData(response.data);
    } catch (error) {
      console.error('Error fetching cost data:', error);
      toast.error('Failed to load cost data');
    } finally {
      setCostLoading(false);
    }
  }, []);

  const addExpense = async () => {
    try {
      await axios.post(
        `${API}/governance/five-pillars/costs/expense`,
        { ...expenseForm, amount: parseFloat(expenseForm.amount) },
        { headers: getHeaders() }
      );
      toast.success('Expense added');
      setShowAddExpense(false);
      setExpenseForm({ ...DEFAULT_EXPENSE_FORM, date: getToday() });
      fetchCostData();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(
        `${API}/governance/five-pillars/costs/expense/${expenseId}`,
        { headers: getHeaders() }
      );
      toast.success('Expense deleted');
      fetchCostData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const addSalary = async () => {
    try {
      await axios.post(
        `${API}/governance/five-pillars/costs/salary`,
        { ...salaryForm, amount: parseFloat(salaryForm.amount) },
        { headers: getHeaders() }
      );
      toast.success('Salary entry added');
      setShowAddSalary(false);
      setSalaryForm({ ...DEFAULT_SALARY_FORM, start_date: getToday() });
      fetchCostData();
    } catch (error) {
      console.error('Error adding salary:', error);
      toast.error('Failed to add salary entry');
    }
  };

  const deleteSalary = async (salaryId) => {
    if (!window.confirm('Delete this salary entry?')) return;
    try {
      await axios.delete(
        `${API}/governance/five-pillars/costs/salary/${salaryId}`,
        { headers: getHeaders() }
      );
      toast.success('Salary entry deleted');
      fetchCostData();
    } catch (error) {
      console.error('Error deleting salary:', error);
      toast.error('Failed to delete salary entry');
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({ ...DEFAULT_EXPENSE_FORM, date: getToday() });
  };

  const resetSalaryForm = () => {
    setSalaryForm({ ...DEFAULT_SALARY_FORM, start_date: getToday() });
  };

  return {
    // State
    showCostModal,
    setShowCostModal,
    costData,
    costLoading,
    showAddExpense,
    setShowAddExpense,
    showAddSalary,
    setShowAddSalary,
    expenseForm,
    setExpenseForm,
    salaryForm,
    setSalaryForm,
    // Actions
    fetchCostData,
    addExpense,
    deleteExpense,
    addSalary,
    deleteSalary,
    resetExpenseForm,
    resetSalaryForm
  };
};

export default useCostManagement;
